/** SegmentManageMenu — per-custom-segment dropdown (rename/recolor/pin/edit/delete). Owns its
 * own DropdownMenuTrigger (icon button) as the Radix anchor → content positions at the chip,
 * not viewport-center. Rendered OUTSIDE role="tablist" — see ProductSegmentBar a11y comment. */
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/v2/ui/DropdownMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/v2/ui/Dialog';
import { Button } from '@/components/v2/ui/Button';
import { Input } from '@/components/v2/ui/Input';
import { SegmentRuleBuilder } from './SegmentRuleBuilder';
import type { CustomSegment, ProductSegmentRule } from './segmentPresets';
import type { ProductTableFilters } from '@/hooks/useProductTableConfig';

const PALETTE = ['#6366f1', '#ef4444', '#22c55e', '#f59e0b', '#3b82f6'];

interface Props {
  segment: CustomSegment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  countFn: (filters: ProductTableFilters) => Promise<number>;
  onRename: (id: string, name: string) => void;
  onRecolor: (id: string, color: string) => void;
  onSetPinned: (id: string, pinned: boolean) => void;
  onUpdateRule: (id: string, rule: ProductSegmentRule) => void;
  onDelete: (id: string) => void;
}

export function SegmentManageMenu({
  segment, open, onOpenChange, countFn,
  onRename, onRecolor, onSetPinned, onUpdateRule, onDelete,
}: Props) {
  const { t } = useTranslation();
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  // Stable wrapper for countFn: prevents live-count debounce resets in SegmentRuleBuilder.
  const stableCount = useCallback((f: ProductTableFilters) => countFn(f), [countFn]);

  const close = () => onOpenChange(false);
  function submitRename() { if (renameDraft.trim()) onRename(segment.id, renameDraft.trim()); setRenameOpen(false); }

  return (
    <>
      <DropdownMenu open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" data-testid="segment-chip-manage"
            aria-label={t('productSegments.menu.aria')} className="h-7 w-7 p-0">
            <MoreVertical className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={() => { setRenameDraft(segment.name); setRenameOpen(true); close(); }}>{t('productSegments.menu.rename')}</DropdownMenuItem>
          <div role="group" aria-label={t('productSegments.menu.recolor')} className="flex gap-1 px-2 py-1">
            {PALETTE.map((c) => (
              <button key={c} type="button" role="menuitemradio" aria-checked={segment.color === c} aria-label={c}
                className="h-5 w-5 rounded-full border border-neutral-300 focus-visible:ring-2 focus-visible:ring-primary-500"
                style={{ backgroundColor: c }} onClick={() => { onRecolor(segment.id, c); close(); }} />
            ))}
          </div>
          <DropdownMenuItem onClick={() => { onSetPinned(segment.id, !segment.pinned); close(); }}>{segment.pinned ? t('productSegments.menu.unpin') : t('productSegments.menu.pin')}</DropdownMenuItem>
          <DropdownMenuItem onClick={() => { setBuilderOpen(true); close(); }}>{t('productSegments.menu.editRules')}</DropdownMenuItem>
          <DropdownMenuItem className="text-red-600" onClick={() => { setDeleteOpen(true); close(); }}>{t('productSegments.menu.delete')}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('productSegments.menu.rename')}</DialogTitle></DialogHeader>
          <Input value={renameDraft} onChange={(e) => setRenameDraft(e.target.value)}
            aria-label={t('productSegments.menu.renameInputLabel')} onKeyDown={(e) => e.key === 'Enter' && submitRename()} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="primary" onClick={submitRename}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('productSegments.menu.deleteConfirm')}</DialogTitle></DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={() => { onDelete(segment.id); setDeleteOpen(false); }}>{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SegmentRuleBuilder open={builderOpen} onClose={() => setBuilderOpen(false)} countFn={stableCount}
        onSave={(name, rule) => { if (name !== segment.name) onRename(segment.id, name); onUpdateRule(segment.id, rule); setBuilderOpen(false); }}
        initialRule={segment.rule} initialName={segment.name} />
    </>
  );
}
